import type { App, BlockDef, DataSource, UiElement, VariableDef } from '@cogo/types-v2'

// Page editing renders the same generated app tree as Preview/runtime, but it
// needs editor-safe presentation rules so app overlays do not hide the page.
// Keep these rules scoped to CanvasViewport edit mode; never apply them to
// application runtime or IDE Preview.
export const PAGE_EDIT_RENDERING_POLICY_CSS = `
  [data-cogo-page-edit-mode="true"] [class*="backdrop-blur"] {
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }

  [data-cogo-page-edit-mode="true"] .fixed.inset-0 {
    position: relative !important;
    inset: auto !important;
    z-index: auto !important;
    min-height: auto !important;
    width: auto !important;
    height: auto !important;
  }

  [data-cogo-page-edit-mode="true"] .fixed.inset-0[class*="bg-background/"],
  [data-cogo-page-edit-mode="true"] .fixed.inset-0[class*="bg-black/"],
  [data-cogo-page-edit-mode="true"] .fixed.inset-0[class*="bg-muted/"],
  [data-cogo-page-edit-mode="true"] .fixed.inset-0[class*="bg-card/"] {
    background: transparent !important;
    background-color: transparent !important;
  }

  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind][data-element-selected="true"],
  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind][data-element-hovered="true"] {
    position: relative !important;
  }

  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind="variable"] {
    display: inline-flex;
    width: fit-content;
    max-width: 100%;
    align-items: center;
    border-radius: 6px;
    border: 1px solid color-mix(in srgb, currentColor 22%, transparent) !important;
    background-color: color-mix(in srgb, currentColor 8%, transparent) !important;
    color: inherit !important;
    box-shadow: inset 0 0 0 1px hsl(var(--background, 0 0% 100%) / 0.45);
    padding: 0.08rem 0.38rem;
    font-size: max(0.62em, 10px);
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: 0;
    vertical-align: baseline;
  }

  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind="variable"][data-cogo-binding-value-type="image-url"] {
    border-color: color-mix(in srgb, currentColor 24%, transparent) !important;
    background-color: color-mix(in srgb, currentColor 9%, transparent) !important;
    color: inherit !important;
  }

  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind="variable"][data-cogo-binding-value-type="number"],
  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind="variable"][data-cogo-binding-value-type="boolean"],
  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind="variable"][data-cogo-binding-value-type="data"] {
    border-color: color-mix(in srgb, currentColor 24%, transparent) !important;
    background-color: color-mix(in srgb, currentColor 10%, transparent) !important;
    color: inherit !important;
  }

  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind][data-element-selected="true"]::after,
  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind][data-element-hovered="true"]::after {
    content: attr(data-cogo-binding-hover-label);
    position: absolute;
    left: 0;
    top: -1.5rem;
    z-index: 40;
    max-width: min(320px, 90vw);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    border-radius: 6px;
    border: 1px solid hsl(var(--border, 214 32% 91%));
    background: hsl(var(--background, 0 0% 100%));
    box-shadow: 0 6px 18px hsl(var(--foreground, 222 47% 11%) / 0.12);
    padding: 2px 6px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.3;
    pointer-events: none;
  }

  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind="static"][data-element-selected="true"]::after,
  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind="static"][data-element-hovered="true"]::after {
    color: hsl(var(--muted-foreground));
  }

  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind="variable"][data-element-selected="true"]::after,
  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind="variable"][data-element-hovered="true"]::after {
    border-color: hsl(38 92% 50% / 0.45);
    color: hsl(32 95% 44%);
  }

  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind="default-variable"][data-element-selected="true"]::after,
  [data-cogo-page-edit-mode="true"] [data-cogo-binding-kind="default-variable"][data-element-hovered="true"]::after {
    border-color: hsl(160 84% 39% / 0.45);
    color: hsl(160 84% 32%);
  }
`

export type PageEditBindingKind = 'static' | 'variable' | 'default-variable'
export type PageEditBindingValueType =
  | 'text'
  | 'image-url'
  | 'number'
  | 'boolean'
  | 'date-time'
  | 'url'
  | 'data'
  | 'value'

export interface PageEditBindingMeta {
  kind: PageEditBindingKind
  label: string
  hoverLabel: string
  tokenLabel: string
  fullLabel: string
  path?: string
  defaultValue?: unknown
  valueType?: PageEditBindingValueType
}

interface BindingContext {
  app: App | null | undefined
  blocks: Record<string, BlockDef | undefined>
}

const EXPR_RE = /#\{([^}]+)\}/g

function expressionParts(value: string): string[] {
  const parts: string[] = []
  for (const match of value.matchAll(EXPR_RE)) {
    const inner = match[1]?.trim()
    if (inner) parts.push(inner)
  }
  return parts
}

function isPlainPath(expr: string): boolean {
  return /^[A-Za-z_$][\w$]*(\.[A-Za-z_$][\w$]*)*$/.test(expr)
}

function readPath(value: unknown, path: string[]): unknown {
  let current = value
  for (const part of path) {
    if (current == null || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[part]
  }
  return current
}

function hasOwnDefault(def: unknown): def is VariableDef {
  return typeof def === 'object' && def !== null && 'default' in def
}

function lookupDefaultInVariableMap(
  map: Record<string, VariableDef> | undefined,
  expr: string,
): { found: boolean; value: unknown } {
  if (!map) return { found: false, value: undefined }
  const parts = expr.split('.')
  for (let i = parts.length; i >= 1; i--) {
    const key = parts.slice(0, i).join('.')
    const def = map[key]
    if (!hasOwnDefault(def)) continue
    const remainder = parts.slice(i)
    const value = remainder.length > 0 ? readPath(def.default, remainder) : def.default
    if (value === undefined) continue
    return {
      found: true,
      value,
    }
  }
  return { found: false, value: undefined }
}

function lookupDefaultInDataMap(
  map: Record<string, DataSource> | undefined,
  expr: string,
): { found: boolean; value: unknown } {
  if (!map) return { found: false, value: undefined }
  const parts = expr.split('.')
  const sourceNames = parts[0] === 'appData' ? [parts[1], parts.slice(1, 3).join('.')] : [parts[0]]
  for (const sourceName of sourceNames) {
    if (!sourceName) continue
    const source = map[sourceName]
    if (!source) continue
    const fallback = source.response?.default ?? source.data
    if (fallback === undefined) continue
    const prefixLength = parts[0] === 'appData'
      ? (sourceName.includes('.') ? 1 + sourceName.split('.').length : 2)
      : sourceName.split('.').length
    const value = readPath(fallback, parts.slice(prefixLength))
    if (value === undefined) continue
    return {
      found: true,
      value,
    }
  }
  return { found: false, value: undefined }
}

export function lookupPageEditBindingDefault(
  expr: string,
  context: BindingContext,
): { found: boolean; value: unknown } {
  if (!isPlainPath(expr)) return { found: false, value: undefined }

  const appDefault = lookupDefaultInVariableMap(context.app?.globalState, expr)
  if (appDefault.found) return appDefault

  for (const block of Object.values(context.blocks)) {
    const state = block?.state
    const stateDefault = lookupDefaultInVariableMap(state?.state, expr)
    if (stateDefault.found) return stateDefault
    const contextDefault = lookupDefaultInVariableMap(
      state?.context as Record<string, VariableDef> | undefined,
      expr,
    )
    if (contextDefault.found) return contextDefault
    const dataDefault = lookupDefaultInDataMap(block?.data, expr)
    if (dataDefault.found) return dataDefault
  }

  const appDataSourceDefault = lookupDefaultInDataMap(context.app?.datasources, expr)
  if (appDataSourceDefault.found) return appDataSourceDefault

  if (expr.startsWith('appData.')) {
    const appDataValue = readPath(context.app?.appData, expr.split('.').slice(1))
    if (appDataValue !== undefined) return { found: true, value: appDataValue }
  }

  return { found: false, value: undefined }
}

function summarizeValue(value: unknown): string {
  if (typeof value === 'string') return value || '""'
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (value === null) return 'null'
  if (Array.isArray(value)) return `[${value.length} items]`
  if (typeof value === 'object') return '{...}'
  return ''
}

function labelForValueType(valueType: PageEditBindingValueType): string {
  switch (valueType) {
    case 'image-url':
      return 'Image URL'
    case 'number':
      return 'Number'
    case 'boolean':
      return 'Boolean'
    case 'date-time':
      return 'Date'
    case 'url':
      return 'URL'
    case 'data':
      return 'Data'
    case 'value':
      return 'Value'
    case 'text':
    default:
      return 'Text'
  }
}

function inferValueType(expr: string, fallback: PageEditBindingValueType): PageEditBindingValueType {
  if (fallback === 'image-url') return 'image-url'
  const segments = expr
    .split('.')
    .map((segment) => segment.toLowerCase())
    .filter((segment) => segment !== 'appdata')
  const last = segments.at(-1) ?? ''
  const hasSegment = (patterns: RegExp[]) => segments.some((segment) => patterns.some((pattern) => pattern.test(segment)))

  if (hasSegment([/image/, /^img$/, /photo/, /avatar/, /cover/, /thumbnail/, /urlimage/, /^src$/])) return 'image-url'
  if (/href|link|url/.test(last)) return 'url'
  if (/count|total|amount|price|number|qty|quantity|subtotal|score|percent|rate/.test(last)) return 'number'
  if (/(pending|active|completed|checked|open|closed).*(tasks|items|events|attendees|users|orders)/.test(last)) return 'number'
  if (/^(is|has|can|should)[a-z0-9_]*/.test(last)) return 'boolean'
  if (/date|time|day|month|year|createdat|updatedat/.test(last)) return 'date-time'
  if (fallback !== 'text' && hasSegment([/^items$/, /^list$/, /^rows$/, /^records$/, /^data$/])) return 'data'
  return fallback
}

function shortenPath(path: string): string {
  if (path.length <= 38) return path
  const parts = path.split('.')
  if (parts.length >= 3) {
    return `${parts[0]}.…${parts.slice(-2).join('.')}`
  }
  return `${path.slice(0, 18)}…${path.slice(-14)}`
}

export function formatPageEditBindingValue(value: string, context: BindingContext): string {
  if (!value.includes('#{')) return value
  return value.replace(EXPR_RE, (_, rawExpr: string) => {
    const expr = rawExpr.trim()
    const defaultValue = lookupPageEditBindingDefault(expr, context)
    if (defaultValue.found) return summarizeValue(defaultValue.value)
    const typeLabel = labelForValueType(inferValueType(expr, 'text'))
    return typeLabel === 'Text' ? 'Variable' : `${typeLabel} variable`
  })
}

function readPrimaryPresentationValue(
  element: UiElement,
): { value: string; fallbackType: PageEditBindingValueType } | null {
  const opts = element.options as Record<string, unknown> | undefined
  const candidates: Array<{ value: unknown; fallbackType: PageEditBindingValueType }> = [
    { value: element.src, fallbackType: 'image-url' },
    { value: opts?.urlImage, fallbackType: 'image-url' },
    { value: element.text, fallbackType: 'text' },
    { value: opts?.text, fallbackType: 'text' },
    { value: element.label, fallbackType: 'text' },
    { value: element.value, fallbackType: 'value' },
    { value: element.placeholder, fallbackType: 'text' },
    { value: element.alt, fallbackType: 'text' },
  ]
  for (const candidate of candidates) {
    if (typeof candidate.value === 'string' && candidate.value.trim().length > 0) {
      return { value: candidate.value, fallbackType: candidate.fallbackType }
    }
  }
  return null
}

function classifyPresentationValue(
  presentation: { value: string; fallbackType: PageEditBindingValueType },
  context: BindingContext,
): PageEditBindingMeta {
  const { value, fallbackType } = presentation
  const expressions = expressionParts(value)
  if (expressions.length === 0) {
    return {
      kind: 'static',
      label: 'Static',
      hoverLabel: 'Static',
      tokenLabel: 'Static',
      fullLabel: 'Static fixed content',
      valueType: fallbackType,
    }
  }

  const firstExpr = expressions[0]
  const defaultResult = lookupPageEditBindingDefault(firstExpr, context)
  const shortPath = shortenPath(firstExpr)
  const valueType = inferValueType(firstExpr, fallbackType)
  const typeLabel = labelForValueType(valueType)
  if (defaultResult.found) {
    const defaultText = summarizeValue(defaultResult.value)
    return {
      kind: 'default-variable',
      label: `Default + ${typeLabel}`,
      hoverLabel: `Default + ${typeLabel} · ${shortPath}`,
      tokenLabel: defaultText,
      fullLabel: `Default + ${typeLabel} variable · ${firstExpr} · default: ${defaultText}`,
      path: firstExpr,
      defaultValue: defaultResult.value,
      valueType,
    }
  }

  return {
    kind: 'variable',
    label: `${typeLabel} variable`,
    hoverLabel: `${typeLabel} variable · ${shortPath}`,
    tokenLabel: valueType === 'text' ? 'Variable' : `${typeLabel} variable`,
    fullLabel: `${typeLabel} variable · ${firstExpr}`,
    path: firstExpr,
    valueType,
  }
}

function metadataKeysForElement(element: UiElement): string[] {
  const keys = new Set([element.id])
  if (element.id.startsWith(`${element.type}-`)) {
    keys.add(element.id.slice(element.type.length + 1))
  }
  return Array.from(keys)
}

function addElementBindingMetadata(
  out: Map<string, PageEditBindingMeta>,
  element: UiElement,
  meta: PageEditBindingMeta,
) {
  for (const [index, key] of metadataKeysForElement(element).entries()) {
    if (index === 0 || !out.has(key)) out.set(key, meta)
  }
}

function collectElementBindingMetadata(
  element: UiElement | undefined,
  context: BindingContext,
  out: Map<string, PageEditBindingMeta>,
) {
  if (!element) return
  const value = readPrimaryPresentationValue(element)
  if (value) addElementBindingMetadata(out, element, classifyPresentationValue(value, context))
  for (const child of element.children ?? []) {
    collectElementBindingMetadata(child, context, out)
  }
}

export function buildPageEditBindingMetadata(
  blocks: Record<string, BlockDef | undefined>,
  context: BindingContext,
): Map<string, PageEditBindingMeta> {
  const out = new Map<string, PageEditBindingMeta>()
  for (const block of Object.values(blocks)) {
    collectElementBindingMetadata(block?.ui?.root, context, out)
  }
  return out
}

export function extractFallbackLiteral(value: string): string | undefined {
  const match = value.match(/\?\?\s*(['"])(.*?)\1/)
  return match?.[2]
}

function lookupPageEditTestDefault(expr: string, block: BlockDef): unknown {
  const normalized = expr.trim()
  const withoutPrefix = normalized.replace(/^(state|local|localState|appData)\./, '')
  const state = block.state as {
    state?: Record<string, VariableDef>
    localState?: Record<string, VariableDef>
  } | undefined
  const stateDefault = lookupDefaultInVariableMap(state?.state, withoutPrefix)
  if (stateDefault.found) return stateDefault.value
  const localDefault = lookupDefaultInVariableMap(state?.localState, withoutPrefix)
  if (localDefault.found) return localDefault.value

  if (normalized.startsWith('appData.')) {
    const value = readPath(block.data, normalized.split('.').slice(1))
    if (typeof value === 'object' && value !== null && 'default' in value) {
      return (value as { default?: unknown }).default
    }
    if (value !== undefined) return value
  }
  return undefined
}

function summarizePageEditToken(valueType: string): string {
  return valueType === 'image-url' ? '{image}' : '{text}'
}

function pageEditValueTypeForKey(key: string, value: string): 'text' | 'image' {
  if (key === 'src') return 'image'
  return inferValueType(expressionParts(value)[0] ?? value, key === 'src' ? 'image-url' : 'text') === 'image-url'
    ? 'image'
    : 'text'
}

function renderPageEditValue(value: string, block: BlockDef, key: string): string {
  const inlineFallback = value.trim().startsWith('#{__i18n.')
    ? extractFallbackLiteral(value)
    : undefined
  if (inlineFallback !== undefined) return value.replace(EXPR_RE, inlineFallback)
  return value.replace(EXPR_RE, (_match, rawExpr: string) => {
    const found = lookupPageEditTestDefault(rawExpr, block)
    if (found !== undefined) return summarizeValue(found)
    return summarizePageEditToken(pageEditValueTypeForKey(key, value))
  })
}

function prepareElementForPageEdit(element: UiElement, block: BlockDef): UiElement {
  const next = structuredClone(element) as UiElement & Record<string, unknown>
  if (typeof next.text === 'string' && next.text.includes('#{')) {
    next.text = renderPageEditValue(next.text, block, 'text')
  }
  if (typeof next.src === 'string' && next.src.includes('#{')) {
    next.src = renderPageEditValue(next.src, block, 'src')
  }
  if (typeof next.bind === 'string') {
    next.defaultValue = summarizePageEditToken('text')
    next.inputType = 'text'
    delete next.bind
  }
  if (Array.isArray(next.children)) {
    next.children = next.children.map((child) => prepareElementForPageEdit(child, block))
  }
  return next
}

export function prepareBlockForPageEdit(block: BlockDef): BlockDef {
  const next = structuredClone(block) as BlockDef
  if (next.ui?.root) next.ui.root = prepareElementForPageEdit(next.ui.root, block)
  return next
}

export function prepareClassNameForPageEdit(className: string): string {
  const classes = className.split(/\s+/).filter(Boolean)
  const filtered = classes.filter((item) =>
    item !== 'fixed' &&
    item !== 'inset-0' &&
    !/^z-\d+$/.test(item) &&
    !/^bg-black\//.test(item) &&
    !/^backdrop-blur/.test(item)
  )
  if (classes.includes('fixed') && !filtered.includes('relative')) filtered.unshift('relative')
  return filtered.join(' ')
}

export interface PageEditCollectedBinding {
  elementId: string
  key: string
  kind: PageEditBindingKind
  valueType: 'text' | 'image'
  source: string
  displayValue?: string
  defaultValue?: unknown
}

function collectBindingForValue(
  out: PageEditCollectedBinding[],
  elementId: string,
  key: string,
  value: string,
  block: BlockDef,
) {
  const expressions = expressionParts(value)
  if (expressions.length === 0 && key === 'bind') expressions.push(value)
  if (expressions.length === 0) return
  const firstExpr = expressions[0]
  const inlineDefault = value.trim().startsWith('#{__i18n.')
    ? extractFallbackLiteral(value)
    : undefined
  const mapDefault = inlineDefault ?? lookupPageEditTestDefault(firstExpr, block)
  const valueType = pageEditValueTypeForKey(key, value)
  out.push({
    elementId,
    key,
    kind: mapDefault !== undefined ? 'default-variable' : 'variable',
    valueType,
    source: key === 'bind' ? `#{${value}}` : `#{${firstExpr}}`,
    displayValue: renderPageEditValue(value, block, key),
    defaultValue: mapDefault,
  })
}

export function collectPageEditBindings(block: BlockDef, root: UiElement): PageEditCollectedBinding[] {
  const out: PageEditCollectedBinding[] = []
  const visit = (element: UiElement) => {
    if (typeof element.text === 'string') collectBindingForValue(out, element.id, 'text', element.text, block)
    if (typeof element.src === 'string') collectBindingForValue(out, element.id, 'src', element.src, block)
    if (typeof element.bind === 'string') collectBindingForValue(out, element.id, 'bind', element.bind, block)
    for (const child of element.children ?? []) visit(child)
  }
  visit(root)
  return out
}
